import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Heart, Shield, Sparkles, Users, MessageCircleHeart, Sun } from "lucide-react";
import { toast } from "sonner";
import AgeGate, { isAgeConfirmed } from "../components/AgeGate";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ThemeSwitcher from "../components/ThemeSwitcher";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function GoogleButton() {
  const { t } = useTranslation();
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/discover";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button
      onClick={handleGoogle}
      data-testid="landing-google-login-btn"
      className="w-full h-12 bg-white text-[#2C2623] border border-[#E8DEC8] hover:bg-[#F9F5EE] rounded-full font-medium shadow-sm">
      <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      {t("auth.continue_google")}
    </Button>
  );
}

function EmailAuthForm({ onSuccess }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login"
        ? { email, password }
        : { email, password, name, age_confirmed: true };
      const { data } = await api.post(url, body);
      // Register returns {user, verification}; login returns the user directly.
      onSuccess(data.user || data, data.verification || null);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tabs value={mode} onValueChange={setMode} className="w-full">
      <TabsList className="grid grid-cols-2 w-full bg-[#F9F5EE]">
        <TabsTrigger value="login" data-testid="auth-tab-login">{t("auth.tab_login")}</TabsTrigger>
        <TabsTrigger value="register" data-testid="auth-tab-register">{t("auth.tab_register")}</TabsTrigger>
      </TabsList>
      <form onSubmit={submit} className="mt-6 space-y-4">
        {mode === "register" && (
          <div className="space-y-2">
            <Label htmlFor="name">{t("auth.name")}</Label>
            <Input id="name" data-testid="auth-name-input" value={name} required
              onChange={(e) => setName(e.target.value)} placeholder={t("auth.name_ph")} className="h-11" />
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" data-testid="auth-email-input" type="email" value={email} required
            onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.email_ph")} className="h-11" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">{t("auth.password")}</Label>
          <Input id="password" data-testid="auth-password-input" type="password" value={password} required
            minLength={6} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.password_ph")} className="h-11" />
        </div>
        {mode === "login" && <Link to="/forgot-password" className="block text-sm text-[#D95D39] underline" data-testid="forgot-password-link">Forgot password?</Link>}
        {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="auth-submit-btn"
          className="w-full h-11 rounded-full bg-[#D95D39] hover:bg-[#C24C29] text-white font-semibold">
          {loading ? t("auth.submit_loading") : mode === "login" ? t("auth.submit_login") : t("auth.submit_register")}
        </Button>
      </form>
    </Tabs>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ageOk, setAgeOk] = useState(isAgeConfirmed());
  const [founding, setFounding] = useState(null);

  useEffect(() => {
    const load = () => api.get("/founding-member/status")
      .then(({ data }) => setFounding(data))
      .catch(() => {});
    load();
    // Keep the counter truly live for visitors on the page.
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const openSignIn = () => {
    setAgeOk(isAgeConfirmed());
    setDialogOpen(true);
  };
  const closeSignIn = (open) => {
    setDialogOpen(open);
  };

  React.useEffect(() => {
    if (user && !window.location.pathname.startsWith("/verify-email")) {
      navigate(user.email_verified === false ? "/verify-email" : "/discover");
    }
  }, [user, navigate]);

  const onAuthSuccess = (data, verification) => {
    setUser(data);
    toast.success(`Welcome, ${data.name?.split(" ")[0] || "friend"}!`);
    if (verification && !data.email_verified) {
      try {
        sessionStorage.setItem("hm_pending_verification", JSON.stringify({ email_sent: !!verification.email_sent }));
      } catch {}
      navigate("/verify-email");
      return;
    }
    navigate("/discover");
  };

  return (
    <div className="min-h-screen bg-[#FFFDF9] relative overflow-hidden">
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#FFF0EC] rounded-full blur-3xl opacity-70" />
      <div className="absolute top-1/2 -left-24 w-80 h-80 bg-[#EAF3F0] rounded-full blur-3xl opacity-60" />

      {/* Founding Member launch banner — visible until 50 real members claim their spot */}
      {founding?.offer_open && (
        <div className="relative z-20 bg-gradient-to-r from-[#D95D39] via-[#E88796] to-[#E09F3E] text-white text-center py-2.5 px-4"
          data-testid="founding-member-banner">
          <span className="font-medium text-sm sm:text-base">
            ✨ {t("landing.founding_banner")}
            <span className="hidden sm:inline">&nbsp;·&nbsp;</span>
            <span className="sm:hidden"><br /></span>
            {t("landing.founding_claimed", { claimed: founding.claimed, total: founding.limit })}
            {" "}{t("landing.founding_remaining", { remaining: founding.remaining })}
          </span>
        </div>
      )}

      {/* Nav */}
      <header className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#D95D39] to-[#E88796] flex items-center justify-center text-white">
            <Heart className="w-4 h-4" strokeWidth={2.5} />
          </span>
          <span className="font-serif text-2xl font-bold text-[#2C2623]">HerpMates</span>
        </div>
        <div className="flex items-center gap-2">
          <ThemeSwitcher />
          <LanguageSwitcher />
          <Button
            onClick={openSignIn}
            data-testid="landing-signin-header-btn"
            className="rounded-full bg-[#2A524A] hover:bg-[#1F3E37] text-white">
            {t("landing.sign_in")}
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#EAF3F0] text-[#2A524A] text-sm font-medium">
              <Sun className="w-4 h-4" /> {t("landing.kicker")}
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold text-[#2C2623] leading-[1.05] tracking-tight">
              {t("landing.hero_title_pre")} <span className="italic text-[#D95D39]">{t("landing.hero_title_word1")}</span>{t("landing.hero_title_mid")} <span className="italic text-[#2A524A]">{t("landing.hero_title_word2")}</span>.
            </h1>
            <p className="text-lg text-[#6E655F] max-w-xl leading-relaxed">
              {t("landing.hero_intro")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md">
              <Button
                onClick={openSignIn}
                data-testid="landing-get-started-btn"
                className="h-14 px-8 rounded-full bg-[#D95D39] hover:bg-[#C24C29] text-white text-base font-semibold shadow-lg shadow-[#D95D39]/20">
                {t("landing.get_started")}
                <Heart className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="landing-learn-btn"
                className="h-14 px-8 rounded-full border-[#E8DEC8] hover:bg-[#F9F5EE] text-[#2C2623]">
                {t("landing.how_it_works")}
              </Button>
            </div>
            <div className="flex items-center gap-6 pt-4">
              <div className="flex -space-x-3">
                {["1534528741775-53994a69daeb", "1507003211169-0a1dd7228f2d", "1517841905240-472988babdf9", "1544005313-94ddf0286df2"].map((id) => (
                  <img key={id} src={`https://images.unsplash.com/photo-${id}?auto=format&fit=crop&q=80&w=100`}
                    className="w-10 h-10 rounded-full border-2 border-white object-cover" alt="member" />
                ))}
              </div>
              {founding?.offer_open ? (
                <div className="flex-1 min-w-0" data-testid="founding-live-counter">
                  <div className="font-semibold text-[#2C2623] flex items-baseline gap-1.5">
                    <span data-testid="founding-live-claimed">{founding.claimed}</span>
                    <span className="text-[#6E655F] font-normal">of</span>
                    <span>{founding.limit}</span>
                    <span className="text-sm text-[#6E655F] font-normal">Founding Member spots claimed</span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-[#F1E7D2] overflow-hidden max-w-[240px]">
                    <div
                      className="h-full bg-gradient-to-r from-[#D95D39] to-[#E09F3E] transition-all duration-500"
                      style={{ width: `${Math.min(100, (founding.claimed / founding.limit) * 100)}%` }}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-semibold text-[#2C2623]">Join a growing community</div>
                  <div className="text-sm text-[#6E655F]">Real people. Real connection. No shame.</div>
                </div>
              )}
            </div>
          </div>

          {/* Right visual */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#D95D39]/10 aspect-[4/5]">
              <img src="https://images.unsplash.com/photo-1784729249196-90f42f34fa08?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGNvdXBsZSUyMHNtaWxpbmclMjByb21hbnRpYyUyMG91dGRvb3JzJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg3NTg4OTk2fDA&ixlib=rb-4.1.0&q=85"
                className="w-full h-full object-cover" alt="Loving couple at sunset" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C2623]/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur rounded-2xl p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FCE8E6] flex items-center justify-center">
                    <MessageCircleHeart className="w-5 h-5 text-[#C5221F]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#6E655F]">AI Icebreaker</div>
                    <div className="text-sm text-[#2C2623] font-medium">"What's the last thing that made you smile today?"</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature cards — each opens its own detail section below */}
      <section id="how" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: Shield, title: "Privacy first", anchor: "privacy",
              body: "Anonymous forum posts and HSV status shared on your terms — never displayed without your say.",
            },
            {
              icon: Sparkles, title: "AI icebreakers", anchor: "ai-icebreakers",
              body: "Never wonder what to say. GPT-powered prompts get real conversations flowing.",
            },
            {
              icon: Users, title: "Community that gets it", anchor: "community",
              body: "Support groups, disclosure tips, and stories from folks living beautifully with HSV.",
            },
          ].map(({ icon: Icon, title, body, anchor }) => (
            <button
              key={anchor}
              type="button"
              data-testid={`feature-card-${anchor}`}
              onClick={() => document.getElementById(`section-${anchor}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="text-left block w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95D39] focus-visible:ring-offset-2 rounded-3xl">
              <Card className="p-8 border-[#E8DEC8] bg-white/60 backdrop-blur rounded-3xl hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer h-full">
                <div className="w-12 h-12 rounded-full bg-[#FFF0EC] flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#D95D39]" />
                </div>
                <h3 className="font-serif text-2xl font-semibold mb-2 text-[#2C2623]">{title}</h3>
                <p className="text-[#6E655F] leading-relaxed">{body}</p>
                <span className="mt-4 inline-flex items-center text-sm font-medium text-[#D95D39]">
                  Learn more <span aria-hidden="true" className="ml-1">→</span>
                </span>
              </Card>
            </button>
          ))}
        </div>
      </section>

      {/* Privacy section */}
      <section id="section-privacy" data-testid="section-privacy"
        className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 scroll-mt-24">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10 items-start">
          <div className="md:col-span-2">
            <div className="w-14 h-14 rounded-full bg-[#EAF3F0] flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-[#2A524A]" />
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C2623] tracking-tight">Privacy first</h2>
            <p className="text-[#6E655F] mt-3 leading-relaxed">
              Your information stays yours. HerpMates was built to feel like a safe room —
              not a public square.
            </p>
          </div>
          <ul className="md:col-span-3 space-y-4">
            {[
              ["Your email is never shown.", "It's used to sign you in — never displayed on your profile or shared with matches."],
              ["Share HSV status on your terms.", "Choose HSV-1, HSV-2, Both, or Private. Change it anytime."],
              ["Anonymous forum posts.", "Toggle 'Post anonymously' when you want to share without a name attached."],
              ["Strictly 18+.", "An age gate at sign-up plus server-side enforcement keeps this a grown-up space."],
              ["You control your photos.", "Upload as many or as few as you like. Remove any of them, anytime."],
            ].map(([h, p]) => (
              <li key={h} className="flex gap-3 p-4 rounded-2xl bg-white/60 border border-[#E8DEC8]">
                <span className="mt-1 w-2 h-2 rounded-full bg-[#2A524A] flex-shrink-0" />
                <div>
                  <div className="font-semibold text-[#2C2623]">{h}</div>
                  <p className="text-[#6E655F] text-sm leading-relaxed">{p}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* AI icebreakers section */}
      <section id="section-ai-icebreakers" data-testid="section-ai-icebreakers"
        className="relative z-10 bg-[#FFF9F3] border-y border-[#E8DEC8] py-20 scroll-mt-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-2">
            <div className="w-14 h-14 rounded-full bg-[#FFF0EC] flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6 text-[#D95D39]" />
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C2623] tracking-tight">AI icebreakers</h2>
            <p className="text-[#6E655F] mt-3 leading-relaxed">
              Once you match, one tap generates three warm, personalised opening
              questions — based on your interests and theirs.
            </p>
            <Button
              onClick={openSignIn}
              data-testid="section-ai-cta-btn"
              className="mt-6 h-11 px-6 rounded-full bg-[#D95D39] hover:bg-[#C24C29] text-white font-semibold">
              Try it after sign-up
            </Button>
          </div>
          <div className="md:col-span-3 space-y-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#B45309] font-semibold px-1">
              <Sparkles className="w-3 h-3" /> AI Spark preview
            </div>
            {[
              "What's the last thing that made you smile today?",
              "If we could teleport for one perfect Sunday, where would we go?",
              "You're both into hiking — what's the best trail you've ever done?",
            ].map((q) => (
              <div key={q} className="bg-white border border-[#E8DEC8] rounded-2xl p-4 text-[#2C2623]">
                {q}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community section */}
      <section id="section-community" data-testid="section-community"
        className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 scroll-mt-24">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10 items-start">
          <div className="md:col-span-2">
            <div className="w-14 h-14 rounded-full bg-[#EAF3F0] flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-[#2A524A]" />
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C2623] tracking-tight">Community that gets it</h2>
            <p className="text-[#6E655F] mt-3 leading-relaxed">
              Five gentle spaces to talk, ask, share and be heard — all inside HerpMates.
            </p>
            <Button
              onClick={openSignIn}
              data-testid="section-community-cta-btn"
              className="mt-6 h-11 px-6 rounded-full bg-[#2A524A] hover:bg-[#1F3E37] text-white font-semibold">
              Join the community
            </Button>
          </div>
          <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ["Newly Diagnosed", "Compassionate first-steps and reassurance."],
              ["Disclosure Tips", "Real scripts and stories from members."],
              ["Relationships & Dating", "Talk about connection, love, and dating with confidence."],
              ["Self-Care & Wellness", "Rituals, foods, movement — what works for the body."],
              ["Medical & Science", "Clear, up-to-date info to stay informed."],
            ].map(([title, body]) => (
              <div key={title} className="bg-white border border-[#E8DEC8] rounded-2xl p-4">
                <div className="font-semibold text-[#2C2623]">{title}</div>
                <p className="text-sm text-[#6E655F] mt-1 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-[#E8DEC8] py-8 text-center text-sm text-[#6E655F]">
        Made with warmth. HerpMates is educational and doesn't provide medical advice.
      </footer>

      {/* Single sign-in dialog shared by every CTA on this page */}
      <Dialog open={dialogOpen} onOpenChange={closeSignIn}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {ageOk ? "Welcome home" : "Quick check"}
            </DialogTitle>
          </DialogHeader>
          {!ageOk ? (
            <AgeGate
              onConfirm={() => setAgeOk(true)}
              onDeny={() => setDialogOpen(false)}
            />
          ) : (
            <div className="space-y-5">
              <GoogleButton />
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-[#E8DEC8]" />
                <span className="text-xs uppercase tracking-widest text-[#6E655F]">or use email</span>
                <div className="flex-1 h-px bg-[#E8DEC8]" />
              </div>
              <EmailAuthForm onSuccess={onAuthSuccess} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
